// RelationshipEditDrawer · slide-over used by the RelationshipPicker
// for inline view + edit. Renders `descriptor.formSchema` and persists
// via `descriptor.updatePath(id)` so the same drawer can service every
// entity type registered in `entityRegistry.js`.
//
// Design: right-side slide-over. Keeps the underlying booking modal
// visible so users never lose their place mid-workflow (user directive,
// Feb 2026).

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Save, Check, AlertCircle, Eye, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function RelationshipEditDrawer({
  open,
  descriptor,
  record,
  mode = "edit",          // "view" | "edit"
  onClose,
  onSaved,                // (updatedRecord) => void
  testIdPrefix = "cc-rel-drawer",
}) {
  const [tab, setTab] = useState(mode);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!open || !record || !descriptor) return;
    setForm(descriptor.toFormShape(record));
    setTab(mode);
    setError(null);
    setOk(false);
  }, [open, record, descriptor, mode]);

  const Icon = descriptor?.icon;
  const canEdit = !!descriptor?.updatePath;

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const save = async () => {
    if (!canEdit) return;
    setSaving(true); setError(null); setOk(false);
    try {
      const payload = descriptor.toUpdatePayload(form);
      const { data } = await api.patch(descriptor.updatePath(record.id), payload);
      setOk(true);
      toast.success(`${descriptor.entityLabel} updated`);
      onSaved && onSaved(data);
      // Give the user a visible confirmation, then close the drawer
      // so they can resume their booking flow uninterrupted.
      setTimeout(() => {
        setOk(false);
        onClose && onClose();
      }, 900);
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!open || !record || !descriptor) return null;

  // Portalized to <body> so the drawer escapes the parent modal's
  // stacking context. Prevents click-interception issues when opened
  // from inside another dialog.
  const drawer = (
    <div
      role="dialog"
      aria-modal="true"
      data-testid={`${testIdPrefix}-overlay`}
      className="fixed inset-0 z-[10000] flex items-stretch justify-end"
      style={{ background: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md h-full overflow-hidden bg-white shadow-[0_30px_60px_-15px_rgba(11,19,43,0.5)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid={`${testIdPrefix}-panel`}
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex items-center gap-3 shrink-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${descriptor.themeTint}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-[#0F172A] truncate">
              {descriptor.labelOf(record)}
            </div>
            <div className="text-[11px] text-[#64748B] truncate">
              {descriptor.entityLabel} · inline {tab}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid={`${testIdPrefix}-close`}
            aria-label="Close"
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* View/Edit tab toggle */}
        {canEdit && (
          <div className="px-4 pt-3 flex items-center gap-1.5 shrink-0">
            <TabBtn active={tab === "view"} onClick={() => setTab("view")} testid={`${testIdPrefix}-tab-view`} Icon={Eye}>View</TabBtn>
            <TabBtn active={tab === "edit"} onClick={() => setTab("edit")} testid={`${testIdPrefix}-tab-edit`} Icon={Pencil}>Edit</TabBtn>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 p-3 text-[13px] flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5" /><div>{error}</div>
            </div>
          )}

          {tab === "view" ? (
            <ViewFields descriptor={descriptor} record={record} testIdPrefix={testIdPrefix} />
          ) : (
            <EditFields
              descriptor={descriptor}
              form={form}
              set={set}
              testIdPrefix={testIdPrefix}
            />
          )}
        </div>

        {/* Footer */}
        {tab === "edit" && canEdit && (
          <div className="p-3 border-t border-slate-100 flex items-center justify-between shrink-0 bg-slate-50/70">
            <div className="text-[11px] text-[#64748B]">
              Changes persist to the {descriptor.entityLabel} library.
            </div>
            <div className="flex items-center gap-2">
              {ok && (
                <span className="text-[12px] text-[#166534] inline-flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" /> Saved
                </span>
              )}
              <button
                onClick={save}
                disabled={saving}
                data-testid={`${testIdPrefix}-save`}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-[#8B5CF6] hover:bg-[#7C3AED] rounded-lg px-4 py-2 disabled:opacity-60"
              >
                {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Saving…</> : <><Save className="w-3.5 h-3.5" />Save</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(drawer, document.body)
    : drawer;
}

function ViewFields({ descriptor, record, testIdPrefix }) {
  const form = descriptor.toFormShape(record);
  return (
    <dl className="space-y-2">
      {descriptor.formSchema.map((f) => {
        const v = form[f.key];
        return (
          <div
            key={f.key}
            data-testid={`${testIdPrefix}-view-${f.key}`}
            className="rounded-lg border border-slate-100 bg-slate-50/70 p-2.5"
          >
            <dt className="text-[10px] font-black tracking-[0.15em] uppercase text-[#64748B]">
              {f.label}
            </dt>
            <dd className="text-[13px] text-[#0F172A] mt-0.5 break-words">
              {v ? String(v) : <span className="text-[#94A3B8] italic">Not set</span>}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function EditFields({ descriptor, form, set, testIdPrefix }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {descriptor.formSchema.map((f) => (
        <div key={f.key} className={f.span === 2 ? "col-span-2" : "col-span-1"}>
          <label className="text-[10px] font-black tracking-[0.15em] uppercase text-[#64748B]">
            {f.label}{f.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {f.type === "textarea" ? (
            <textarea
              data-testid={`${testIdPrefix}-input-${f.key}`}
              value={form[f.key] ?? ""}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={f.placeholder}
              rows={3}
              className="w-full mt-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]/25 resize-none"
            />
          ) : (
            <input
              data-testid={`${testIdPrefix}-input-${f.key}`}
              type={f.type || "text"}
              value={form[f.key] ?? ""}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={f.placeholder}
              className="w-full mt-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]/25"
            />
          )}
        </div>
      ))}
    </div>
  );
}

function TabBtn({ active, onClick, testid, Icon, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-full px-3 py-1 transition-colors ${
        active ? "bg-[#8B5CF6] text-white" : "bg-slate-100 text-[#475569] hover:bg-slate-200"
      }`}
    >
      <Icon className="w-3 h-3" />
      {children}
    </button>
  );
}
